import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { getUnregisteredUserConfig } from '@/config/unregisteredUser.config';
import useStateHandler from '@/hooks/useStateHandler';
import MandatoryPopupResultsService from '@/services/mandatoryPopupResults.service';
import ArtworkImage from '@/shared-components/cards/components/ArtworkImage';
import SuarteName from '@/shared-components/icons/components/public/SuarteName';

import GenericPopup from '../components/GenericPopup';

import GoogleButton from './components/GoogleButton';
import SingUpLogInButton from './components/SingUpLogInButton';
import { UNREGISTERED_POPUP_OPEN_LOCK_KEY, UNREGISTERED_POPUP_PRESENTED_EVENT, UNREGISTERED_POPUP_PRESENTED_KEY, UNREGISTERED_POPUP_STORAGE_KEY } from './constants';

import './index.css';

const FALLBACK_IMG = 'GzjV6OCKjJgqiM4UmTMhqC3wbAp2_648ebb932a82c10af7e7f1c6';
const LS_VARIANT_KEY = 'unreg_popup_last_variant_idx';
const isBrowser = typeof window !== 'undefined';
const RESULT_TYPES = {
  EXIT: 'exit',
  LOGIN_INTENT: 'login',
};

// ===== helpers =====
function pickRandomIndex(len, lastIdx) {
  if (len <= 1) return 0;
  let idx = Math.floor(Math.random() * len);
  if (lastIdx != null && idx === lastIdx) idx = (idx + 1) % len;
  return idx;
}

// si tu ArtworkImage construye URL por id, esto suele ser correcto
const CF_BASE = 'https://suarte.art/cdn-cgi/imagedelivery/hAeIC__6Aj746x0RFU1joA';
const resolveArtworkSrc = (id) => {
  if (!id) return '';
  // si ya viene URL completa, úsala tal cual
  if (/^https?:\/\//i.test(id)) return id;
  // si es un id de CF, usamos la variante "public"
  return `${CF_BASE}/${id}/public`;
};

// descarga + decodifica (para evitar flash al pintar)
const safeDecode = (img) => {
  if (typeof img.decode === 'function') {
    // Si decode falla (Safari/Android antiguos), lo ignoramos sin romper eslint
    return img.decode().catch(() => undefined);
  }
  return Promise.resolve();
};

const preloadAndDecodeImage = (src) =>
  new Promise((resolve) => {
    if (!src) return resolve();
    const img = new Image();
    img.onload = async () => {
      await safeDecode(img);
      resolve();
    };
    img.onerror = () => resolve(); // no bloqueamos si falla
    img.src = src;
  });

const waitFonts = () =>
  typeof document !== 'undefined' && document.fonts && document.fonts.ready
    ? document.fonts.ready.catch(() => {})
    : Promise.resolve();

const delay = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * props:
 *  - showPopUp?: forzar apertura (opcional)
 *  - reason?: 'rest' | 'like_artwork' | 'follow' | 'profile' | 'purchase' | 'rating' | 'thought'
 *  - username?: para {username}
 *  - artworkName?: para {artwork_name}
 *  - customVariants?: lista opcional de { title?, image? } para sobreescribir las variantes
 */
export default function UnregisteredPopup({
  showPopUp = false,
  containerClassName = '',
  subtitleContent = null,
  bottomTextContent = null,
  children = null,
  containerType = 'default',
  imageIdOverride = null,
  customVariants = null,
}) {
  const navigate = useNavigate();
  const { state } = useStateHandler();
  const cfg = getUnregisteredUserConfig();

  // estados de control de render
  const [ready, setReady] = useState(false); // imagen + fuentes + delay listos
  const [dismissed, setDismissed] = useState(false); // si se cierra manualmente
  const [selectedTitle, setSelectedTitle] = useState('');
  const [selectedImageId, setSelectedImageId] = useState('');
  const hasTrackedResultRef = useRef(false);

  // ¿debe mostrarse? (auto por clicks o forzado por prop)
  const shouldShow = useMemo(() => {
    if (dismissed) return false;
    if (showPopUp) return true;
    if (state?.user_session || !isBrowser) return false;
    // Solo mostrar si está “ready” y NO hay otro popup abierto
    const ready = localStorage.getItem(UNREGISTERED_POPUP_STORAGE_KEY) === 'true';
    const someoneOpen = localStorage.getItem(UNREGISTERED_POPUP_OPEN_LOCK_KEY) === 'true';
    return ready && !someoneOpen;
  }, [showPopUp, state?.user_session, dismissed]);

  // Mantener correspondencia 1:1 entre texto e imagen por índice
  const defaultTitle = useMemo(() => cfg?.title || 'Welcome to Suarte', [cfg]);
  const defaultImage = useMemo(() => imageIdOverride || cfg?.guest_image || FALLBACK_IMG, [cfg, imageIdOverride]);

  const variants = useMemo(() => {
    if (Array.isArray(customVariants) && customVariants.length > 0) {
      return customVariants.map((variant) => ({
        title: variant?.title || defaultTitle,
        image: variant?.image || defaultImage,
      }));
    }
    const candidateTitles = [cfg?.title, cfg?.title_2, cfg?.title_3];
    const candidateImages = [cfg?.guest_image, cfg?.guest_image_2, cfg?.guest_image_3];
    const pairedVariants = candidateTitles
      .map((title, idx) => ({ title, image: candidateImages[idx] }))
      .filter((variant) => variant.title || variant.image)
      .map((variant) => ({
        title: variant.title || defaultTitle,
        image: variant.image || defaultImage,
      }));

    if (pairedVariants.length) return pairedVariants;

    return [{ title: defaultTitle, image: defaultImage }];
  }, [cfg, customVariants, defaultTitle, defaultImage]);

  const variantsSignature = useMemo(
    () => variants.map(({ title, image }) => `${title || ''}-${image || ''}`).join('|'),
    [variants],
  );
  const variantCacheKey = useMemo(
    () => `${LS_VARIANT_KEY}_${containerType || 'default'}`,
    [containerType],
  );

  // Elegir variante + precargar + esperar fuentes + delay de animación
  useEffect(() => {
    let cancelled = false;
    setReady(false);

    if (!shouldShow) return;
    hasTrackedResultRef.current = false;

    const lastVariantValue = isBrowser ? localStorage.getItem(variantCacheKey) : null;
    const lastVariantIdx = Number.isFinite(Number(lastVariantValue))
      ? Number(lastVariantValue)
      : null;

    const variantIdx = variants.length ? pickRandomIndex(variants.length, lastVariantIdx) : 0;
    const selectedVariant = variants[variantIdx] || {};

    const chosenTitle = selectedVariant.title || defaultTitle;
    const chosenImageId = selectedVariant.image || defaultImage;
    const chosenSrc = resolveArtworkSrc(chosenImageId);

    if (variants.length && isBrowser) localStorage.setItem(variantCacheKey, String(variantIdx));

    (async () => {
      // Espera: imagen decodificada + fuentes listas + pequeño delay de animación
      await Promise.all([preloadAndDecodeImage(chosenSrc), waitFonts(), delay(200)]);
      if (cancelled) return;
      setSelectedTitle(chosenTitle);
      setSelectedImageId(chosenImageId);
      MandatoryPopupResultsService.rememberLastContext({
        title: chosenTitle,
        image: chosenImageId,
        containerType,
      });
      if (isBrowser) {
        localStorage.setItem(UNREGISTERED_POPUP_PRESENTED_KEY, 'true');
        window.dispatchEvent(new Event(UNREGISTERED_POPUP_PRESENTED_EVENT));
      }
      setReady(true);
      // Marcamos el lock cuando realmente vamos a abrir
      if (isBrowser) {
        localStorage.setItem(UNREGISTERED_POPUP_OPEN_LOCK_KEY, 'true');
      }
    })();

    return () => {
      cancelled = true;
      // Si desmonta en medio del proceso, liberamos el lock por si quedó marcado
      if (isBrowser) {
        localStorage.removeItem(UNREGISTERED_POPUP_OPEN_LOCK_KEY);
      }
    };

  }, [shouldShow, variantsSignature, variantCacheKey]);

  const trackResult = useCallback((result) => {
    if (!result || hasTrackedResultRef.current) return;
    hasTrackedResultRef.current = true;
    MandatoryPopupResultsService.rememberLastContext({
      title: selectedTitle || null,
      image: selectedImageId || null,
      containerType,
    });

    MandatoryPopupResultsService.createMandatoryPopupResult({
      title: selectedTitle || null,
      image: selectedImageId || null,
      result,
      containerType,
    }).catch((error) => {
      console.error('Failed to persist mandatory popup result', error);
    });
  }, [selectedTitle, selectedImageId, containerType]);

  const closePopup = useCallback(() => {
    setDismissed(true);
    setReady(false);
    if (isBrowser) {
      localStorage.removeItem(UNREGISTERED_POPUP_OPEN_LOCK_KEY);
    }
  }, []);

  const closeWithResult = useCallback((result = RESULT_TYPES.EXIT) => {
    trackResult(result);
    closePopup();
  }, [trackResult, closePopup]);

  // Entre pestañas/ventanas: si otro instancia pone el lock, nos cerramos.
  useEffect(() => {
    if (!isBrowser) return;
    const onStorage = (e) => {
      if (e.key === UNREGISTERED_POPUP_OPEN_LOCK_KEY && e.newValue === 'true') {
        setDismissed(true);
        setReady(false);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => () => {
    if (ready && !hasTrackedResultRef.current) {
      trackResult(RESULT_TYPES.EXIT);
    }
  }, [ready, trackResult]);

  // 🔒 CLAVE: no montamos el popup hasta que esté TODO listo
  if (!shouldShow || !ready) return null;

  const resolvedSubtitle = subtitleContent ?? (
    <>
      Continue on <SuarteName className="suarte-img" /> for free
    </>
  );

  const resolvedBottomText = bottomTextContent ?? (
    <>
      By continuing, you agree to Suarte’s{' '}
      <span
        className="terms-and-conditions"
        onClick={() => navigate('/terms', { state: { from: true } })}
      >
        terms and conditions
      </span>{' '}
      and confirm you are 18 or older.
    </>
  );

  const containerClasses = ['container-unregistered', containerClassName].filter(Boolean).join(' ');

  return (
    <GenericPopup
      open={true}
      darkerBlur
      opacity
      className="center-unregistered-popup"
      onBackgroundClick={() => closeWithResult(RESULT_TYPES.EXIT)}
    >
      <div className={containerClasses}>
        <div className="container-image-and-title">
          <ArtworkImage
            image={selectedImageId || FALLBACK_IMG}
            imageClassName="imagen"
          />
          <div className="container-title">
            <h1 className="title">{selectedTitle || 'Welcome to Suarte'}</h1>
          </div>
        </div>

        <span className="subtitle">
          {resolvedSubtitle}
        </span>

        <GoogleButton
          className="unregistered-user__google-button"
          close={closeWithResult}
        />
        <SingUpLogInButton
          onIntent={() => closeWithResult(RESULT_TYPES.LOGIN_INTENT)}
        />

        {children}

        <span className="bottom-text">
          {resolvedBottomText}
        </span>
      </div>
    </GenericPopup>
  );
}
